import { useEffect, useState, type FormEvent } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation, type Store, type SupabaseClient } from '@dukanti/shared';
import { useUpdateStoreNewsletterCollection } from '@dukanti/shared/admin';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useNavBlocker } from '@/hooks/use-nav-blocker';
import { supabase } from '@/lib/supabase';
import { useStoreNewsletterCollectionPermissions } from './use-store';

export interface NewsletterCollectionFormProps {
  readonly store: Store;
  readonly client?: SupabaseClient;
}

/** Store-owned opt-in stays outside the content-only website editor. */
export function NewsletterCollectionForm({
  store,
  client = supabase,
}: NewsletterCollectionFormProps): React.JSX.Element {
  const { t, dir } = useTranslation();
  const permissions = useStoreNewsletterCollectionPermissions(store.id, client);
  const mutation = useUpdateStoreNewsletterCollection(client);
  const [enabled, setEnabled] = useState(store.newsletter_collection_enabled === true);
  const [failed, setFailed] = useState(false);
  const savedEnabled = store.newsletter_collection_enabled === true;
  const isDirty = enabled !== savedEnabled;
  const canManage = !permissions.isError && permissions.data?.canManageCollection === true;
  useNavBlocker(isDirty || mutation.isPending);

  useEffect(() => {
    setEnabled(store.newsletter_collection_enabled === true);
    setFailed(false);
  }, [store.id, store.newsletter_collection_enabled]);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canManage || !isDirty || mutation.isPending) return;
    setFailed(false);
    try {
      // mutateAsync includes the awaited Store/editor/canvas/public query refresh.
      await mutation.mutateAsync({ storeId: store.id, enabled });
      toast.success(t('admin.storeSettings.newsletterCollection.savedToast'));
    } catch {
      setFailed(true);
    }
  }

  return (
    <form
      dir={dir}
      onSubmit={(event) => void submit(event)}
      className="mt-6"
      data-testid="newsletter-collection-form"
      aria-busy={mutation.isPending}
    >
      <section className="bg-card border-border rounded-xl border p-4 shadow-sm sm:p-8">
        <h2 className="font-display text-foreground text-start text-xl font-bold">
          {t('admin.storeSettings.newsletterCollection.title')}
        </h2>
        <p className="text-muted-foreground mt-2 text-start text-sm">
          {t('admin.storeSettings.newsletterCollection.description')}
        </p>
        <Label
          htmlFor="newsletter-collection-enabled"
          className="border-border bg-background mt-5 flex min-h-11 items-start gap-3 rounded-lg border p-4 text-start"
        >
          <input
            id="newsletter-collection-enabled"
            type="checkbox"
            checked={enabled}
            disabled={!canManage || mutation.isPending}
            onChange={(event) => {
              setEnabled(event.target.checked);
              setFailed(false);
            }}
            aria-describedby="newsletter-collection-hint"
            className="border-input text-admin-lime focus:ring-admin-lime mt-1 size-4 shrink-0 rounded"
            data-testid="newsletter-collection-checkbox"
          />
          <span className="min-w-0">
            <span className="text-foreground block text-sm font-medium">
              {t('admin.storeSettings.newsletterCollection.label')}
            </span>
            <span
              id="newsletter-collection-hint"
              className="text-muted-foreground mt-1 block text-sm"
            >
              {t(
                enabled
                  ? 'admin.storeSettings.newsletterCollection.enabledHint'
                  : 'admin.storeSettings.newsletterCollection.disabledHint',
              )}
            </span>
          </span>
        </Label>
        {permissions.isLoading ? (
          <p role="status" className="text-muted-foreground mt-3 text-sm">
            {t('common.loading')}
          </p>
        ) : permissions.isError ? (
          <div
            role="alert"
            className="mt-3 space-y-2"
            data-testid="newsletter-collection-permission-error"
          >
            <p className="text-destructive text-sm">{t('common.errors.networkFailed')}</p>
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              onClick={() => void permissions.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        ) : !canManage ? (
          <p
            className="text-muted-foreground mt-3 text-sm"
            data-testid="newsletter-collection-permission-hint"
          >
            {t('admin.storeSettings.newsletterCollection.permissionHint')}
          </p>
        ) : null}
        {failed ? (
          <div
            role="alert"
            className="text-destructive mt-4 flex items-start gap-2 text-sm"
            data-testid="newsletter-collection-error"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>{t('admin.storeSettings.newsletterCollection.error')}</span>
          </div>
        ) : null}
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <a
            href="#newsletter-subscribers"
            className="text-foreground inline-flex min-h-11 items-center rounded text-sm underline focus-visible:outline focus-visible:outline-2"
          >
            {t('admin.storeSettings.newsletterCollection.manageSubscribers')}
          </a>
          {canManage ? (
            <div className="flex flex-wrap gap-2">
              {isDirty ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="min-h-11"
                  disabled={mutation.isPending}
                  onClick={() => {
                    setEnabled(savedEnabled);
                    setFailed(false);
                  }}
                >
                  {t('common.cancel')}
                </Button>
              ) : null}
              <Button
                type="submit"
                className="min-h-11"
                data-testid="newsletter-collection-save"
                disabled={!isDirty || mutation.isPending}
              >
                {mutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : null}
                {t(
                  mutation.isPending
                    ? 'admin.storeSettings.newsletterCollection.saving'
                    : 'common.save',
                )}
              </Button>
            </div>
          ) : null}
        </div>
      </section>
    </form>
  );
}
